import React, { useEffect, useMemo, useState } from 'react'
import { FormProvider, useForm, useWatch } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'

import {
  Button,
  Flex,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/react'

import { DatePicker, FieldWrapper, Input, Select } from '@/components/Form'

// Logic
import { hideLoadingOverlay, showLoadingOverlay } from '@/stores/ui/loadingOverlaySlice'

//API
import useUser from '@/features/users/useUser'
import { authSelector } from '@/stores/auth/authSlice'

//Slice
import { hideCreateOfferModal, selectCreateOfferModal } from '@/stores/offers/offersSlice'

//Constant
import { ContributorGroupType } from '@/features/contributorGroups/constants'
import { useCostCenter } from '@/features/costCenters/useCostCenter'
import { OfferType } from '@/features/offers/constants'
import useOffer from '@/features/offers/hooks/useOffer'
import { createOptions } from '@/utils/arrayHelper'
import { excelFileTypes } from '@/utils/constants'
import { camelToWords } from '@/utils/stringHelper'

const CreateOfferModal = () => {
  const { show, offerType: modalOfferType } = useSelector(selectCreateOfferModal)
  const { user } = useSelector(authSelector)

  const [file, setFile] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const dispatch = useDispatch()

  // Hook
  const navigate = useNavigate()
  const methods = useForm()
  const offerType = useWatch({ name: 'offerType', control: methods.control })
  const createType = useWatch({ name: 'createType', control: methods.control })

  const { useCreateOffer, useImportOffer } = useOffer()
  const { useFetchAllCostCenters } = useCostCenter()
  const { useUserOptions } = useUser()

  const createOffer = useCreateOffer()
  const importOffer = useImportOffer()

  const { data: costCenterResponse } = useFetchAllCostCenters({ page: 1, pageSize: 0, filters: { status: 'active' } })

  const { data: userOptions } = useUserOptions({
    valueKey: '_id',
    labelKey: 'fullName',
    contributorGroup: [ContributorGroupType.TECHNICAL_PROJECT_MANAGER, 'sales', 'engineer'],
  })

  const costCenterOptions = useMemo(() => {
    if (!Array.isArray(costCenterResponse?.data)) {
      return []
    }

    const filteredCostCenters = costCenterResponse.data.filter((costCenter) => costCenter.type === offerType)

    return createOptions(
      filteredCostCenters,
      (costCenter) => `${costCenter.profitCenterCode || ''} - ${costCenter.name || ''}`,
      '_id'
    )
  }, [offerType, costCenterResponse?.data])
  useEffect(() => {
    methods.reset({
      offerType: modalOfferType ?? '',
    })
  }, [modalOfferType])
  useEffect(() => {
    if (createType) {
      //createType Change, clear start, end date
      const allValues = methods.getValues()
      const salesResponsible = createType === 'new' ? user?._id : undefined
      methods.reset({
        ...allValues,
        projectStartDate: undefined,
        projectEndDate: undefined,
        salesResponsible,
      })
    }
  }, [createType, methods, user])

  const closeModal = () => {
    methods.reset({
      projectName: '',
      createType: '',
      profitCenter: '',
      projectStartDate: undefined,
      projectEndDate: undefined,
      salesResponsible: undefined,
    })

    dispatch(hideCreateOfferModal())
  }

  const onSubmit = async (data) => {
    try {
      setIsSubmitting(true)
      if (data.createType === 'import') {
        if (!file) {
          toast.error('No File Uploaded')
          return
        }
        dispatch(showLoadingOverlay())
        const res = await importOffer.mutateAsync({
          offerType: data.offerType,
          file,
        })
        methods.reset({ createType: '', projectName: '', profitCenter: '' })
        closeModal()
        if (res._id && (res?.createdBy === user?._id || res?.creator === user?._id)) {
          navigate(`/offers/${res._id}`)
        }
      } else {
        dispatch(showLoadingOverlay())
        const res = await createOffer.mutateAsync({
          type: data.offerType,
          projectName: data.projectName,
          profitCenter: data.profitCenter,
          projectStartDate: data.projectStartDate,
          projectEndDate: data.projectEndDate,
          salesResponsible: data.salesResponsible,
        })
        closeModal()
        if (res._id && (res?.createdBy === user?._id || res?.creator === user?._id)) {
          navigate(`/offers/${res._id}`)
        }
      }
    } catch (err) {
    } finally {
      setIsSubmitting(false)

      dispatch(hideLoadingOverlay())
    }
  }

  const handleFileChange = (e) => {
    if (e.target.files.length > 0) {
      setFile(e.target.files[0])
    }
  }

  return (
    <Modal isOpen={show} onClose={closeModal}>
      <ModalOverlay />
      <ModalContent maxW={'30%'}>
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)}>
            <ModalHeader>Create {camelToWords(offerType)} Offer</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Flex direction={'column'} gap="4">
                <Select
                  name="offerType"
                  label="Offer Type"
                  options={[
                    {
                      value: 'maintenance',
                      label: 'Maintenance',
                    },

                    {
                      value: 'project',
                      label: 'Project',
                    },
                  ]}
                  isRow
                  disabled={offerType && offerType !== ''}
                />
                <Select
                  name="createType"
                  label="Create Method"
                  options={[
                    {
                      value: 'new',
                      label: 'Create Blank',
                    },

                    {
                      value: 'import',
                      label: 'Create import',
                    },
                  ]}
                  isRow
                />
                {createType === 'new' && (
                  <>
                    <Input name="projectName" label="Project Name" isRow placeholder="Enter value" />

                    <Select name="profitCenter" label="Profit Center" options={costCenterOptions} isRow />
                    <Select name="salesResponsible" label="Sales Responsible" isRow options={userOptions} />
                  </>
                )}

                {createType === 'new' && offerType === OfferType.MAINTENANCE && (
                  <>
                    <DatePicker name="projectStartDate" label="Project Start Date" isRow needPortal={false} />
                    <DatePicker name="projectEndDate" label="Project End Date" isRow needPortal={false} />
                  </>
                )}

                {createType === 'import' && (
                  <>
                    <FieldWrapper label="Excel File" isRow>
                      <input type="file" accept={excelFileTypes} onChange={handleFileChange} />
                    </FieldWrapper>
                  </>
                )}
              </Flex>
            </ModalBody>

            <ModalFooter>
              <HStack>
                <Button colorScheme="green" type="submit" isLoading={isSubmitting} isDisabled={isSubmitting}>
                  Create
                </Button>

                <Button colorScheme="gray" onClick={closeModal}>
                  Close
                </Button>
              </HStack>
            </ModalFooter>
          </form>
        </FormProvider>
      </ModalContent>
    </Modal>
  )
}

export default CreateOfferModal
